import type { LoginRequest } from '@avrahami/contracts';
import {
  Loading,
  MFA_PENDING,
  safeRedirect,
  SIGNED_OUT,
  signedIn,
  SignInScreen,
} from '@avrahami/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect } from 'react';
import { sessionApi, sessionQuery } from '../api/session';
import { Centered } from '../components/Layout';
import { he } from '../i18n/he';
import { errorMessage } from '../lib/forms';
import { homePath } from '../lib/nav';
import { sessionChanged } from '../lib/session-sync';
import { goToStore, tabHref, tabStore } from '../lib/store-path';

/**
 * Password first; when the account has a second factor, the code step follows. Store staff are
 * not forced to enroll one (they may turn it on in "my account").
 */
export function LoginPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const requested = useSearch({ from: '/login' }).redirect;
  const session = useQuery(sessionQuery);
  const me = session.data?.kind === 'signed-in' ? session.data.me : undefined;

  // Already signed in (another tab of this store, or right after the second step): go where the
  // member was headed, or to the first screen their role can use. A session in another store
  // than this tab's opens at that store's address (adr/0033).
  useEffect(() => {
    if (me === undefined) return;
    const store = me.activeTenant;
    const home = homePath(store?.capabilities ?? []);
    if (store !== null && store.tenantId !== tabStore) {
      // A tab without a store keeps the screen it was asked for; another store's does not.
      const target = tabStore === undefined ? safeRedirect(requested, home) : home;
      goToStore(store.tenantId, target, true);
      return;
    }
    void navigate({ href: safeRedirect(requested, tabHref(home)), replace: true });
  }, [me, requested, navigate]);

  if (session.isPending || me !== undefined) {
    return (
      <Centered>
        <Loading />
      </Centered>
    );
  }

  // Nothing cached for a previous member (or store) survives a new sign-in, in any tab.
  const login = async (body: LoginRequest) => {
    const result = await sessionApi.login(body);
    sessionChanged(
      queryClient,
      result.status === 'MFA_REQUIRED' ? MFA_PENDING : signedIn(result.me),
    );
    return result;
  };
  const verify = async (code: string) => {
    sessionChanged(queryClient, signedIn(await sessionApi.verifyMfa(code)));
  };
  const startOver = async () => {
    try {
      await sessionApi.logout();
    } finally {
      sessionChanged(queryClient, SIGNED_OUT);
    }
  };

  return (
    <SignInScreen
      product={he.app.product}
      words={he.login}
      devCodeWords={he.devCode}
      mfaPending={session.data?.kind === 'mfa-pending'}
      login={login}
      verify={verify}
      startOver={startOver}
      errorText={errorMessage}
      forgotLink={
        <Link to="/forgot-password" className="text-center text-sm text-brand-700 underline">
          {he.forgotPassword.link}
        </Link>
      }
    />
  );
}
